import { hasTargetThatCanBeDisabled } from './../../../common/utils/event-typeguards';
import { hasFiles } from '../../../common/utils/event-typeguards';
import { JobApplicationAndDocuments } from './../../model/jobapplicationanddocuments';
import { Active, Category } from 'app/rm/erp/jobapplication/model/bwmconfig';
import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { HisNotificationService, HisUiService } from 'hisng';
import { FileSizeUtils } from 'app/rm/erp/common/utils/filesize-utils';
import { Document } from 'app/rm/erp/common/document/model/document';
import { DocumentCategoryValue } from 'app/rm/erp/common/document/model/document-values';
import { JobApplicationService } from 'app/rm/erp/jobapplication/service/jobapplication.service';
import { ErpDocumentService } from 'app/rm/erp/common/document/service/erp-document-service';
import { FileUpload } from 'primeng/fileupload';
import { JobApplication } from '../../model/jobapplication';

@Component( {
    selector: 'erp-jobapplication-form',
    templateUrl: './jobapplication-form.component.html',
} )
export class JobApplicationFormComponent implements OnInit {
    @Input() jobApplication: JobApplicationAndDocuments = new JobApplicationAndDocuments();
    @Output() readonly onJobApplicationCreated = new EventEmitter<never>();
    @Output() readonly valueChange = new EventEmitter<never>();
    @Output() readonly onDeleteJobApplication = new EventEmitter<never>();

    documentCategoryValues: DocumentCategoryValue[] = [];
    filteredDocumentCategoryValues: DocumentCategoryValue[] = [];

    documentCategory: DocumentCategoryValue | undefined;

    toggleUploadButtonDisabled = true;

    categories: Category[] = [];

    constructor(
        private readonly hisUiService: HisUiService,
        private readonly jobApplicationService: JobApplicationService,
        private readonly notificationService: HisNotificationService,
        private readonly documentService: ErpDocumentService,
        private readonly fileSizeUtils: FileSizeUtils
    ) { }

    ngOnInit() {
        if ( this.jobApplication.application.jobAdvertisementId ) {
            this.jobApplicationService.getJobApplicationConfig( this.jobApplication.application.jobAdvertisementId )
                .subscribe( bwmConfig => {
                    bwmConfig.categories.forEach( ( category ) => {
                        if ( category.active === Active.Y ) {
                            this.categories.push( category );
                        }
                    } );
                } );
        }

        this.documentService.getDocumentCategoryValues().subscribe( documentCategoryValues => {
            this.documentCategoryValues = documentCategoryValues;
        } );
    }

    customUploadHandler( event: unknown, fileUpload: FileUpload ) {
        if ( hasFiles( event ) ) {
            const isValid: boolean = this.validateUploadedFiles( event.files, fileUpload );

            if ( isValid ) {
                this.addFiles( event.files, fileUpload );
            }

            this.updateUploadedFileCount( fileUpload );
        }
    }

    async addFiles( files: File[], fileUpload: FileUpload ) {
        for ( const file of files ) {
            const buffer = await file.arrayBuffer();
            const doc: Document = {
                filename: file.name,
                filesize: file.size + '',
                file: Array.from( new Uint8Array( buffer ) ),
                documentCategory: this.documentCategory
            };
            this.jobApplication.documents.push( doc );

            this.updateUploadedFileCount( fileUpload );
        }
    }

    /* Aktualisiert die FileUploadkomponente. Wichtig ist dabei der Aufruf der (method) ChangeDetectorRef.markForCheck()
       zum Aktualisieren des Uploadbuttons (siehe markForCheck-Beschreibung) */
    updateUploadedFileCount( fileUpload: FileUpload ) {
        fileUpload.uploadedFileCount = this.jobApplication.documents.length;
        fileUpload.files = [];
        fileUpload.cd.markForCheck();
    }

    validateUploadedFiles( files: File[], fileUpload: FileUpload ): boolean {
        const isFileWithSameNamePresent: boolean = this.isFileWithSameNamePresent( files );

        if ( isFileWithSameNamePresent ) {
            if (fileUpload.msgs) {
                fileUpload.msgs.push( {
                    severity: 'error',
                    summary: this.hisUiService.translate( 'rm.erp.common.documents.upload.filenameExists' ),
                    detail: ''
                } );
            }

            return false;
        }

        if ( !this.documentCategory ) {
            this.showErrorMessage( 'activerecord.errors.messages.blank' );
            return false;
        }

        return true;
    }

    removeFile( event: Event, index: number, fileUpload: FileUpload ) {
        if ( index > -1 ) {
            this.jobApplication.documents.splice( index, 1 );
            fileUpload.remove( event, index );
            this.updateUploadedFileCount( fileUpload );
        }
    }

    validateJobApplication( event: UIEvent ) {
        if ( hasTargetThatCanBeDisabled( event ) ) {
            // Der Button soll deaktiviert werden, bis die Validierung durch das Backend abgeschlossen ist
            event.target.disabled = true;

            this.notificationService.clear();
            this.jobApplicationService.validateJobApplication( this.jobApplication ).subscribe({
                next: () => {
                    event.target.disabled = false;
                    this.onJobApplicationCreated.emit();
                },
                error: () => {
                    event.target.disabled = false;
                } });
        }
    }

    /* Filtere Dokumentenkategorien danach, ob Feld qis_bewerbermanagement_relevant ("relevantForBwm") gesetzt ist. */
    relevantDocumentCategoryValues() {
        this.filteredDocumentCategoryValues = [];
        this.documentCategoryValues.forEach( documentCategoryValue => {
            if ( documentCategoryValue.relevantForBwm ) {
                this.filteredDocumentCategoryValues.push( documentCategoryValue );
            }
        } );
    }

    fetchRelevantValues( name: string ) {
        switch ( name ) {
            case 'documentCategory':
                this.relevantDocumentCategoryValues();
                break;
        }
    }

    toggleDocumentUploadButton( event: unknown, fileUpload: FileUpload ) {
        if ( event == null ) {
            this.toggleUploadButtonDisabled = true;
            return;
        }
        this.toggleUploadButtonDisabled = false;
        this.updateUploadedFileCount( fileUpload );
    }

    getDocumentUploadHelptext( fileLimit: number, maxFileSize: number, fileTypes: string ): string {
        const maxFileSizeMB = this.fileSizeUtils.convertByteToMegaByte( Number( maxFileSize ) );
        return this.hisUiService.translate( 'rm.erp.common.documents.uploadFile.informationtext', fileLimit, maxFileSizeMB, fileTypes );
    }

    showErrorMessage( messageKey: string ): void {
        this.notificationService.error(
            this.hisUiService.translate( messageKey ),
            this.hisUiService.translate( 'fs.res.common.service.error' ), 5000
        );
    }

    isFileWithSameNamePresent( files: File[] ): boolean {
        return this.jobApplication.documents.findIndex( document =>
            files.findIndex( file => file.name === document.filename ) !== -1
        ) !== -1;
    }

    deleteJobApplication( _: UIEvent ) {
        this.onDeleteJobApplication.emit();
    }

    onChange( _: JobApplication ): void {
        this.valueChange.emit();
    }
}
